import { useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { useAuth } from '@/auth/context'
import { NEXT_PARAM, safeNext } from '@/auth/routes'
import { MIN_PASSWORD } from '@/lib/password'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { GoogleButton } from '@/ui/GoogleButton'
import { PasswordField } from '@/ui/PasswordField'
import { InlineError } from '@/ui/states'
import { PublicPage } from './PublicPage'

type Route = 'email' | 'mobile'

/**
 * `/signup` — S-D2 screens 01 and 02.
 *
 * **Three ways in, and they are alternatives rather than a set** (§4). An agency
 * owner will press Google; a shop owner in Hyderabad will use their mobile
 * number, and that account has no email address at all — which is why
 * `users.email` is nullable and why this is a toggle rather than two fields.
 *
 * The password rules are shown before the first submit, not after a rejection,
 * and the strength meter says what it can honestly measure — see
 * `lib/password.ts` for why it does not claim to check breach lists.
 *
 * The terms checkbox is now enforced on **both** sides, and S-B12 is why. When
 * this page was written the consent table did not exist and a server-side check
 * would have been a flag nothing stores — worse than an honest client-side gate,
 * because it would look like a record. `public.consent_records` exists now, so
 * the box is sent with the request, the API refuses a signup without it, and
 * three versioned rows are written in the same transaction that creates the
 * account. DPDP requires consent to be **demonstrable**; a checkbox this
 * component enforced alone demonstrates nothing.
 *
 * The client-side check stays, because the two do different jobs: this one puts
 * the message next to the control the person did not tick, and the server's one
 * is what makes the record true.
 */
export function Signup(): JSX.Element {
  const { adopt } = useAuth()
  const [params, setParams] = useSearchParams()
  const invited = safeNext(params.get(NEXT_PARAM))

  const [route, setRoute] = useState<Route>('email')
  const [email, setEmail] = useState('')
  const [mobile, setMobile] = useState('')
  const [password, setPassword] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [agreementError, setAgreementError] = useState<string | null>(null)

  // Where a successful signup lands. An invitation link brought its own `next`
  // and keeps it — dropping that person at "check your inbox" would strand the
  // invitation.
  const destination = invited ?? (route === 'mobile' ? '/verify-mobile' : '/check-email')

  const withEmail = useMutation(
    (body: { email: string; password: string; accepted_terms: boolean }) =>
      call(() => api.POST('/api/v1/auth/signup', { body })),
  )
  const withMobile = useMutation(
    (body: { mobile: string; password: string; accepted_terms: boolean }) =>
      call(() => api.POST('/api/v1/auth/signup/mobile', { body })),
  )

  const pending = withEmail.pending || withMobile.pending
  const failure = withEmail.state.failure ?? withMobile.state.failure

  function clearFailures(): void {
    withEmail.reset()
    withMobile.reset()
  }

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    if (!agreed) {
      setAgreementError('Please accept the terms to create an account.')
      return
    }
    setAgreementError(null)

    // The destination goes into `next` *before* the request, and that is not a
    // flourish — it is the only ordering that is deterministic. `adopt()` is an
    // urgent state update while a navigation is a router transition, so the
    // instant the session lands `RequireAnonymous` re-renders at `/signup` with
    // an authenticated user and redirects **before** any navigation this
    // component started can commit. That sent every new account past the screen
    // it was meant to see. Setting `next` first makes the guard's own redirect
    // the right one, so there is nothing left to race.
    const updated = new URLSearchParams(params)
    updated.set(NEXT_PARAM, destination)
    setParams(updated, { replace: true })

    // A mobile account is unusable until the number is verified, so the OTP
    // screen is where that signup goes. Both routes end the same way: adopt the
    // identity and let the guard carry us to `destination`.
    const result =
      route === 'mobile'
        ? await withMobile.run({ mobile: mobile.trim(), password, accepted_terms: true })
        : await withEmail.run({
            email: email.trim().toLowerCase(),
            password,
            accepted_terms: true,
          })
    if (result.ok) adopt({ user: result.data.user, session: result.data.session })
  }

  return (
    <PublicPage
      title="Start your 14-day trial"
      footer={
        <>
          Already have an account?{' '}
          <Link to={invited ? `/login?${NEXT_PARAM}=${encodeURIComponent(invited)}` : '/login'}>
            Sign in
          </Link>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <div className="ar-card__body ar-stack">
          <GoogleButton label="Continue with Google" acceptedTerms={agreed} />

          <div className="app-seg" role="group" aria-label="Sign up with">
            {(['email', 'mobile'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={route === option}
                onClick={() => {
                  setRoute(option)
                  clearFailures()
                }}
              >
                {option === 'email' ? 'Email' : 'Mobile'}
              </button>
            ))}
          </div>

          <InlineError failure={failure} />

          {route === 'email' ? (
            <TextField
              label="Work email"
              type="email"
              required
              autoFocus
              autoComplete="email"
              placeholder="you@company.in"
              help="We send a confirmation link here. Approval requests and alerts go to it too."
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
                clearFailures()
              }}
            />
          ) : (
            <TextField
              label="Mobile number"
              type="tel"
              required
              autoFocus
              autoComplete="tel"
              inputMode="tel"
              placeholder="+91 98490 12345"
              help="Include the country code. We text a 6-digit code to confirm it."
              value={mobile}
              onChange={(event) => {
                setMobile(event.target.value)
                clearFailures()
              }}
            />
          )}

          <PasswordField
            label="Password"
            required
            minLength={MIN_PASSWORD}
            autoComplete="new-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value)
              clearFailures()
            }}
          />

          <div className="ar-field" data-state={agreementError ? 'invalid' : undefined}>
            <label className="ar-choice ar-choice--check">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(event) => {
                  setAgreed(event.target.checked)
                  setAgreementError(null)
                }}
              />
              <span className="ar-choice__box" aria-hidden="true" />
              <span className="ar-choice__text">
                I agree to the <a href="https://augrank.com/terms">Terms</a> and{' '}
                <a href="https://augrank.com/privacy">Privacy Policy</a>, including the{' '}
                <a href="https://augrank.com/dpdp">DPDP notice</a>.
              </span>
            </label>
            {agreementError ? (
              <span className="ar-field__error">
                <span aria-hidden="true">!</span>
                {agreementError}
              </span>
            ) : null}
          </div>
        </div>
        <div className="ar-card__footer">
          <span className="ar-card__spacer" />
          <Button
            tone="primary"
            size="lg"
            block
            type="submit"
            busy={pending}
            busyLabel="Creating account"
          >
            Create account
          </Button>
        </div>
      </form>
    </PublicPage>
  )
}
